/**
 * PromptCard Component
 *
 * Main input area for image generation with textarea, aspect ratio select,
 * image count select, improve button, and generate button.
 * Includes attached image mode for editing existing images.
 */

import { Sparkles, Wand2, X, Loader2 } from 'lucide-react';
import { SectionHeader } from './SectionHeader';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AVATAR_CONSTANTS, type IPromptCardProps, type TAspectRatio } from '@/@types/avatars';

const ASPECT_RATIOS: { value: TAspectRatio; label: string }[] = [
  { value: '1:1', label: '1:1' },
  { value: '9:16', label: '9:16' },
  { value: '16:9', label: '16:9' },
  { value: '4:5', label: '4:5' },
];

const IMAGE_COUNTS: (1 | 2 | 3 | 4)[] = [1, 2, 3, 4];

export const PromptCard = ({
  prompt,
  onPromptChange,
  selectedRatio,
  onRatioChange,
  imageCount,
  onImageCountChange,
  attachedImage,
  onRemoveAttachment,
  onImprove,
  onGenerate,
  isGenerating,
  isImproving = false,
  disabled,
}: IPromptCardProps) => {
  const charCount = prompt.length;
  const maxLength = AVATAR_CONSTANTS.MAX_PROMPT_LENGTH;

  return (
    <div className="flex-1 rounded-2xl border border-border bg-card p-5">
      <SectionHeader
        step={2}
        label="Image Prompt"
        rightElement={
          <span className="text-xs text-muted-foreground">
            {charCount}/{maxLength}
          </span>
        }
      />

      <div className="mt-4 flex flex-col gap-4">
        {/* Textarea */}
        <Textarea
          value={prompt}
          onChange={e => onPromptChange(e.target.value)}
          placeholder="Describe the image you want to generate..."
          maxLength={maxLength}
          disabled={isGenerating}
          className="min-h-[80px] resize-none bg-background-input rounded-lg"
        />

        {/* Attached Image Section (shown when editing) */}
        {attachedImage && (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-background-input border border-primary/30">
            <img
              src={attachedImage.url}
              alt="Attached for editing"
              className="w-[60px] h-[80px] rounded-md object-cover border-2 border-primary"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2 text-primary">
                <Wand2 className="w-4 h-4" />
                <span className="text-sm font-medium">Editing attached image</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Describe changes you want to make...
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onRemoveAttachment}
              disabled={isGenerating}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}

        {/* Button Row */}
        <div className="flex items-center gap-3">
          {/* Improve with AI Button */}
          <Button
            variant="outline"
            onClick={onImprove}
            disabled={disabled || isGenerating || isImproving || !prompt.trim()}
            className="w-[180px] bg-[#1a2e1a] hover:bg-[#243824] dark:bg-[#1a2e1a] dark:hover:bg-[#243824] border-[#2a4a2a] text-primary"
          >
            {isImproving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            <span>{isImproving ? 'Improving...' : 'Improve with AI'}</span>
          </Button>

          {/* Aspect Ratio Select */}
          <Select
            value={selectedRatio}
            onValueChange={v => onRatioChange(v as TAspectRatio)}
            disabled={disabled || isGenerating}
          >
            <SelectTrigger className="w-[100px]" aria-label="Aspect ratio">
              <SelectValue placeholder="Ratio" />
            </SelectTrigger>
            <SelectContent>
              {ASPECT_RATIOS.map(ratio => (
                <SelectItem key={ratio.value} value={ratio.value}>
                  {ratio.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Image Count Select */}
          <Select
            value={String(imageCount)}
            onValueChange={v => onImageCountChange(Number(v) as 1 | 2 | 3 | 4)}
            disabled={disabled || isGenerating}
          >
            <SelectTrigger className="w-[100px]" aria-label="Image count">
              <SelectValue placeholder="Images" />
            </SelectTrigger>
            <SelectContent>
              {IMAGE_COUNTS.map(count => (
                <SelectItem key={count} value={String(count)}>
                  {count} {count === 1 ? 'Image' : 'Images'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Generate Button */}
          <Button
            onClick={onGenerate}
            disabled={disabled || isGenerating || !prompt.trim()}
            className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                <span>Generate</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
